/**
 * @typedef {Object} MediaGallerySlideDetail
 * @property {HTMLElement & {dataset: {mediaId: string}}} currentElement
 */

/**
 * @typedef {CustomEvent<MediaGallerySlideDetail>} MediaGallerySlideEvent
 */

/**
 * @typedef {HTMLElement & {
 *   slider?: HTMLElement,
 *   sliderItems?: HTMLElement[],
 *   resetPages?: () => void,
 *   isSlideVisible?: (element: HTMLElement, threshold: number) => boolean
 * }} SliderElement
 */

/**
 * @typedef {HTMLElement & {
 *   loadContent: (autoplay: boolean) => void
 * }} DeferredMediaElement
 */

if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        /** @type {{liveRegion: Element | null, viewer: SliderElement | null, thumbnails: SliderElement | null}} */
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: /** @type {SliderElement | null} */ (this.querySelector('[id^="GalleryViewer"]')),
          thumbnails: /** @type {SliderElement | null} */ (this.querySelector('[id^="GalleryThumbnails"]')),
        };
        /** @type {MediaQueryList} */
        this.mql = window.matchMedia('(min-width: 750px)');
        /** @type {HTMLElement | null} */
        this.stickyHeader = null;
        if (!this.elements.thumbnails) {return;}

        const viewer = /** @type {SliderElement} */ (this.elements.viewer);
        viewer.addEventListener('slideChanged', /** @type {EventListener} */ (debounce(this.onSlideChanged.bind(this), 500)));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const element = /** @type {HTMLElement} */ (mediaToSwitch);
          const button = element.querySelector('button');
          if (button) {
            button.addEventListener('click', this.setActiveMedia.bind(this, element.dataset.target, false));
          }
        });
        if (this.dataset.desktopLayout && this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) {this.removeListSemantic();}
      }

      /**
       * @param {MediaGallerySlideEvent} event
       */
      onSlideChanged(event) {
        if (!this.elements.thumbnails) {return;}
        const thumbnail = /** @type {HTMLElement | null} */ (this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        ));
        this.setActiveThumbnail(thumbnail);
      }

      /**
       * @param {string} mediaId
       * @param {boolean} prepend
       */
      setActiveMedia(mediaId, prepend) {
        if (!this.elements.viewer) {return;}
        const activeMedia = /** @type {HTMLElement | null} */ (this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`));
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        if (!activeMedia) {return;}
        activeMedia.classList.add('is-active');

        if (prepend) {
          if (activeMedia.parentElement) {
            activeMedia.parentElement.prepend(activeMedia);
          }
          if (this.elements.thumbnails) {
            const activeThumbnail = /** @type {HTMLElement | null} */ (this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`));
            if (activeThumbnail && activeThumbnail.parentElement) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }
          if (this.elements.viewer.slider && this.elements.viewer.resetPages) {this.elements.viewer.resetPages();}
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.elements.thumbnails && activeMedia.parentElement) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
            activeMedia.scrollIntoView({ behavior: 'smooth' });
          }
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) {return;}
        const activeThumbnail = /** @type {HTMLElement & {dataset: {mediaPosition?: string}}} */ (this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`));
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition || '');
      }

      /**
       * @param {HTMLElement | null} thumbnail
       */
      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) {return;}

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        const button = thumbnail.querySelector('button');
        if (button) {
          button.setAttribute('aria-current', 'true');
        }
        if (this.elements.thumbnails.isSlideVisible && this.elements.thumbnails.isSlideVisible(thumbnail, 10)) {return;}

        if (this.elements.thumbnails.slider) {
          this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
        }
      }

      /**
       * @param {HTMLElement} activeItem
       * @param {string} position
       */
      announceLiveRegion(activeItem, position) {
        const image = /** @type {HTMLImageElement | null} */ (activeItem.querySelector('.product__modal-opener--image img'));
        if (!image) {return;}
        image.onload = () => {
          if (this.elements.liveRegion) {
            this.elements.liveRegion.setAttribute('aria-hidden', 'false');
            this.elements.liveRegion.textContent = /** @type {{accessibilityStrings: {imageAvailable: string}}} */ (/** @type {unknown} */ (window)).accessibilityStrings.imageAvailable.replace('[index]', position);
            setTimeout(() => {
              if (this.elements.liveRegion) {
                this.elements.liveRegion.setAttribute('aria-hidden', 'true');
              }
            }, 2000);
          }
        };
        // Force image reload by reassigning src
        const currentSrc = image.src;
        image.src = currentSrc;
      }

      /**
       * @param {HTMLElement} activeItem
       */
      playActiveMedia(activeItem) {
        /** @type {{pauseAllMedia: () => void}} */ (/** @type {unknown} */ (window)).pauseAllMedia();
        const deferredMedia = /** @type {DeferredMediaElement | null} */ (activeItem.querySelector('.deferred-media'));
        if (deferredMedia) {deferredMedia.loadContent(false);}
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || /** @type {HTMLElement | null} */ (document.querySelector('sticky-header'));
        if (!this.stickyHeader) {return;}
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer || !this.elements.viewer.slider) {return;}
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        if (this.elements.viewer.sliderItems) {
          this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
        }
      }
    }
  );
}
